
  import { defineComponent, onMounted, ref, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import MonacoEditor from '@guolao/vue-monaco-editor';

  export default defineComponent({
    name: 'ads-brokerage-configuration-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      MonacoEditor,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const titleEnContent = ref('');
      const titleArContent = ref('');
      const descriptionEnContent = ref('');
      const descriptionArContent = ref('');
      const logoContent = ref('');
      const thumbnailContent = ref('');

      const logosInput = ref<File[]>([]);
      const logoPreviews = ref<string[]>([]);
      const logoThumbnail = ref('/media/avatars/blank-image.jpg');
      const existingLogoUrls = ref<string[]>([]);
      const logosToRemove = ref<string[]>([]);

      const thumbnailsInput = ref<File[]>([]);
      const thumbnailPreviews = ref<string[]>([]);
      const thumbnailPreviewThumbnail = ref('/media/avatars/blank-image.jpg');
      const existingThumbnailUrls = ref<string[]>([]);
      const thumbnailsToRemove = ref<string[]>([]);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const removeExistingLogo = (index: number) => {
        const logoUrl = existingLogoUrls.value[index];
        logosToRemove.value.push(logoUrl);
        existingLogoUrls.value.splice(index, 1);

        const logoJson: any = {};
        existingLogoUrls.value.forEach((url, idx) => {
          logoJson[`value${idx + 1}`] = url;
        });
        logoContent.value = JSON.stringify(logoJson, null, 2);
      };

      const removeExistingThumbnail = (index: number) => {
        const thumbnailUrl = existingThumbnailUrls.value[index];
        thumbnailsToRemove.value.push(thumbnailUrl);
        existingThumbnailUrls.value.splice(index, 1);

        const thumbnailJson: any = {};
        existingThumbnailUrls.value.forEach((url, idx) => {
          thumbnailJson[`value${idx + 1}`] = url;
        });
        thumbnailContent.value = JSON.stringify(thumbnailJson, null, 2);
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('adsBrokerageConfigUpdate'), [
          translate('ads'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_ADS_BROKERAGE_CONFIG,
        route.params.id
      );

      const rawConfig = data.data;

      const config = ref<any>({
        id: rawConfig.id,
        brokerName: rawConfig.brokerName,
        agentName: rawConfig.agentName,
        phone: rawConfig.phone,
      });

      if (rawConfig.titleEn) {
        titleEnContent.value = JSON.stringify(rawConfig.titleEn, null, 2);
      }
      if (rawConfig.titleAr) {
        titleArContent.value = JSON.stringify(rawConfig.titleAr, null, 2);
      }
      if (rawConfig.descriptionEn) {
        descriptionEnContent.value = JSON.stringify(
          rawConfig.descriptionEn,
          null,
          2
        );
      }
      if (rawConfig.descriptionAr) {
        descriptionArContent.value = JSON.stringify(
          rawConfig.descriptionAr,
          null,
          2
        );
      }

      if (rawConfig.logo) {
        logoContent.value = JSON.stringify(rawConfig.logo, null, 2);

        const logoJson = rawConfig.logo;
        existingLogoUrls.value = Object.keys(logoJson)
          .filter((key) => key.startsWith('value'))
          .map((key) => logoJson[key])
          .filter((url) => url);
      }

      if (rawConfig.thumbnail) {
        thumbnailContent.value = JSON.stringify(rawConfig.thumbnail, null, 2);

        const thumbnailJson = rawConfig.thumbnail;
        existingThumbnailUrls.value = Object.keys(thumbnailJson)
          .filter((key) => key.startsWith('value'))
          .map((key) => thumbnailJson[key])
          .filter((url) => url);
      }

      const previewLogos = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files) {
          logosInput.value = Array.from(files);
          logoPreviews.value = [];

          for (let i = 0; i < files.length; i++) {
            const reader = new FileReader();
            reader.onload = (e) => {
              logoPreviews.value.push(e.target?.result as string);
              if (i === 0) {
                logoThumbnail.value = e.target?.result as string;
              }
            };
            reader.readAsDataURL(files[i]);
          }
        }
      };

      const previewThumbnails = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files) {
          thumbnailsInput.value = Array.from(files);
          thumbnailPreviews.value = [];

          for (let i = 0; i < files.length; i++) {
            const reader = new FileReader();
            reader.onload = (e) => {
              thumbnailPreviews.value.push(e.target?.result as string);
              if (i === 0) {
                thumbnailPreviewThumbnail.value = e.target?.result as string;
              }
            };
            reader.readAsDataURL(files[i]);
          }
        }
      };

      const removeLogo = (index: number) => {
        logoPreviews.value.splice(index, 1);

        const dt = new DataTransfer();
        const files = logosInput.value;

        for (let i = 0; i < files.length; i++) {
          if (i !== index) {
            dt.items.add(files[i]);
          }
        }

        logosInput.value = Array.from(dt.files);

        if (logoPreviews.value.length > 0) {
          logoThumbnail.value = logoPreviews.value[0];
        } else {
          logoThumbnail.value = '/media/avatars/blank-image.jpg';
        }
      };

      const removeThumbnail = (index: number) => {
        thumbnailPreviews.value.splice(index, 1);

        const dt = new DataTransfer();
        const files = thumbnailsInput.value;

        for (let i = 0; i < files.length; i++) {
          if (i !== index) {
            dt.items.add(files[i]);
          }
        }

        thumbnailsInput.value = Array.from(dt.files);

        if (thumbnailPreviews.value.length > 0) {
          thumbnailPreviewThumbnail.value = thumbnailPreviews.value[0];
        } else {
          thumbnailPreviewThumbnail.value = '/media/avatars/blank-image.jpg';
        }
      };

      const uploadImages = async (
        configId: number,
        files: File[],
        imageType: 'logo' | 'thumbnail'
      ) => {
        if (!files || files.length === 0) {
          return null;
        }

        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append('images', files[i]);
        }

        try {
          const response = await store.dispatch(
            Actions.UPLOAD_ADS_BROKERAGE_LOGO,
            {
              data: formData,
              id: configId,
              imageType,
            }
          );

          return response;
        } catch (error) {
          console.error(`${imageType} upload error:`, error);
          return null;
        }
      };

      const getUpdatedImageUrls = async (
        configId: number,
        imageType: 'logo' | 'thumbnail'
      ): Promise<string[]> => {
        try {
          const { data } = await store.dispatch(
            Actions.GET_ADS_BROKERAGE_CONFIG,
            configId
          );

          if (data.data && data.data[imageType]) {
            const imageJson = data.data[imageType];

            const urls = Object.keys(imageJson)
              .filter((key) => key.startsWith('value'))
              .map((key) => imageJson[key])
              .filter((url) => url);

            return urls;
          }

          return [];
        } catch (error) {
          console.error(`Error fetching updated ${imageType} URLs:`, error);
          return [];
        }
      };

      const configSchema = Yup.object().shape({
        brokerName: Yup.string()
          .required(() => translate('BROKER_NAME_IS_REQUIRED_FIELD'))
          .label('Broker Name'),
        agentName: Yup.string()
          .required(() => translate('AGENT_NAME_IS_REQUIRED_FIELD'))
          .label('Agent Name'),
        phone: Yup.string()
          .required(() => translate('PHONE_IS_REQUIRED_FIELD'))
          .label('Phone'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        try {
          await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

          const titleEn = JSON.parse(titleEnContent.value);
          const titleAr = JSON.parse(titleArContent.value);
          const descriptionEn = JSON.parse(descriptionEnContent.value);
          const descriptionAr = JSON.parse(descriptionArContent.value);

          const dataToSubmit: any = {
            brokerName: values.brokerName,
            agentName: values.agentName,
            titleEn: titleEn,
            titleAr: titleAr,
            descriptionEn: descriptionEn,
            descriptionAr: descriptionAr,
            phone: values.phone,
          };

          if (logosToRemove.value.length > 0) {
            dataToSubmit.logosToRemove = logosToRemove.value;
          }

          if (thumbnailsToRemove.value.length > 0) {
            dataToSubmit.thumbnailsToRemove = thumbnailsToRemove.value;
          }

          await store.dispatch(Actions.UPDATE_ADS_BROKERAGE_CONFIG, {
            id: route.params.id,
            data: dataToSubmit,
          });

          if (logosInput.value && logosInput.value.length > 0) {
            const uploadResponse = await uploadImages(
              Number(route.params.id),
              logosInput.value,
              'logo'
            );

            if (uploadResponse) {
              const logoUrls = await getUpdatedImageUrls(
                Number(route.params.id),
                'logo'
              );

              if (logoUrls.length > 0) {
                const logoJson: any = {};
                logoUrls.forEach((url, index) => {
                  logoJson[`value${index + 1}`] = url;
                });
                logoContent.value = JSON.stringify(logoJson, null, 2);
                existingLogoUrls.value = logoUrls;
              }
            }
          }

          if (thumbnailsInput.value && thumbnailsInput.value.length > 0) {
            const uploadResponse = await uploadImages(
              Number(route.params.id),
              thumbnailsInput.value,
              'thumbnail'
            );

            if (uploadResponse) {
              const thumbnailUrls = await getUpdatedImageUrls(
                Number(route.params.id),
                'thumbnail'
              );

              if (thumbnailUrls.length > 0) {
                const thumbnailJson: any = {};
                thumbnailUrls.forEach((url, index) => {
                  thumbnailJson[`value${index + 1}`] = url;
                });
                thumbnailContent.value = JSON.stringify(thumbnailJson, null, 2);
                existingThumbnailUrls.value = thumbnailUrls;
              }
            }
          }

          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];

          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );

          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
            submitButton.value?.removeAttribute('data-kt-indicator');
            submitButton.value!.disabled = false;
          } else {
            Swal.fire({
              text: translate('SUCCESSFULLY_UPDATED_ADS_BROKERAGE_CONFIG'),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            }).then(() => {
              router.push({ name: 'ads-brokerage-configuration-listing' });
            });
          }
        } catch (e) {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          console.error('Error:', e);
          Swal.fire({
            text: translate('ERROR_PROCESSING_DATA'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        configSchema,
        goBack,
        config,
        can,
        titleEnContent,
        titleArContent,
        descriptionEnContent,
        descriptionArContent,
        logoContent,
        thumbnailContent,
        logosInput,
        logoPreviews,
        logoThumbnail,
        existingLogoUrls,
        thumbnailsInput,
        thumbnailPreviews,
        thumbnailPreviewThumbnail,
        existingThumbnailUrls,
        previewLogos,
        previewThumbnails,
        removeLogo,
        removeThumbnail,
        removeExistingLogo,
        removeExistingThumbnail,
      };
    },
  });
